import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import Avatar from './Avatar';
import LanguageSelector from './LanguageSelector';
import {
  IoPeopleOutline,
  IoPeopleSharp,
  IoChatbubbleOutline,
  IoChatbubbleSharp,
  IoLogOutOutline,
  IoGlobeOutline,
  IoSearchOutline,
  IoCloseOutline,
  IoDocumentTextOutline,
  IoPersonOutline,
  IoChevronDownOutline,
  IoStatsChartOutline,
  IoStatsChartSharp
} from 'react-icons/io5';
import { useChat } from '../contexts/ChatContext';
import { useAdmin } from '../contexts/AdminContext';
import { searchApi } from '../services/searchApi';
import pushService from '../services/pushService';
import NotificationDropdown from './NotificationDropdown';
import './Layout.css';

const Layout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, logout, pendingRequestsCount } = useAuth();
  const { totalUnread } = useChat();
  const { hasDashboard } = useAdmin();
  const navigate = useNavigate();
  const location = useLocation();

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ users: [], posts: [] });
  const [showSearch, setShowSearch] = useState(false);
  const [searching, setSearching] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const searchTimeoutRef = useRef(null);
  const searchRef = useRef(null);
  const userMenuRef = useRef(null);
  const phoneUserMenuRef = useRef(null);

  // Handle search
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.length < 2) {
      setSearchResults({ users: [], posts: [] });
      setShowSearch(false);
      return;
    }

    setSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await searchApi.search(query, 5);
        setSearchResults({
          users: results?.users || [],
          posts: results?.posts || []
        });
        setShowSearch(true);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearch(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target) &&
          phoneUserMenuRef.current && !phoneUserMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize push notifications when authenticated
  useEffect(() => {
    console.log('[Layout] Push init - isAuthenticated:', isAuthenticated, 'pushSupported:', pushService.isPushSupported());
    if (isAuthenticated && pushService.isPushSupported()) {
      console.log('[Layout] Starting push subscription...');
      // Call directly - subscribeToPush has its own token waiting logic
      pushService.subscribeToPush()
        .then((sub) => {
          console.log('[Layout] Push subscription result:', sub ? 'success' : 'failed/null');
        })
        .catch((err) => {
          console.error('[Layout] Failed to initialize push:', err);
        });
    }
  }, [isAuthenticated]);

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults({ users: [], posts: [] });
    setShowSearch(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogoClick = () => {
    navigate('/', { replace: true });
    window.scrollTo(0, 0);
  };

  return (
    <div className="layout">
      <header className="header">
        <div className="header-content">
          <a href="/" onClick={(e) => { e.preventDefault(); handleLogoClick(); }} className="logo">
            <img src="/header-icon.png" alt="emoozi" className="logo-icon" />
          </a>

          {/* Search bar */}
          {isAuthenticated && (
            <div className="header-search" ref={searchRef}>
              <div className="search-input-wrapper">
                <IoSearchOutline className="search-icon" />
                <input
                  type="text"
                  className="search-input"
                  placeholder={t('search.placeholder')}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchQuery.length >= 2) {
                      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
                      clearSearch();
                    }
                  }}
                />
                {searchQuery && (
                  <button className="search-clear" onClick={clearSearch}>
                    <IoCloseOutline />
                  </button>
                )}
              </div>

              {/* Search results dropdown - only users */}
              {showSearch && searchResults.users.length > 0 && (
                <div className="search-dropdown">
                  <div className="search-section">
                    <div className="search-section-title">{t('search.users')}</div>
                    {searchResults.users.map(u => (
                      <div
                        key={u.id}
                        className="search-result-item"
                        onClick={() => { navigate(`/profile/${u.id}`); clearSearch(); }}
                      >
                        <Avatar user={u} size="small" />
                        <div className="search-result-info">
                          <div className="search-result-name">{u.display_name || u.username}</div>
                          <div className="search-result-username">@{u.username}</div>
                        </div>
                      </div>
                    ))}
                    {searchQuery.length >= 2 && (
                      <div
                        className="search-result-item search-see-more"
                        onClick={() => { navigate(`/search?q=${encodeURIComponent(searchQuery)}`); clearSearch(); }}
                      >
                        <div className="search-result-info">
                          <div className="search-result-name">{t('search.seeMore')}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {isAuthenticated && (
            <>
              {/* Desktop nav - visible on desktop */}
              <div className="header-right">
                <nav className="nav desktop-nav">
                  <Link
                    to="/friends"
                    className={isActive('/friends') ? 'nav-link active' : 'nav-link'}
                    data-tooltip={t('friends.friends')}
                  >
                    {isActive('/friends') ? <IoPeopleSharp /> : <IoPeopleOutline />}
                    {pendingRequestsCount > 0 && <span className="nav-badge">{pendingRequestsCount > 99 ? '99+' : pendingRequestsCount}</span>}
                  </Link>
                  <Link
                    to="/chat"
                    className={isActive('/chat') ? 'nav-link active' : 'nav-link'}
                    data-tooltip={t('chat.messages')}
                  >
                    {isActive('/chat') ? <IoChatbubbleSharp /> : <IoChatbubbleOutline />}
                    {totalUnread > 0 && <span className="nav-badge">{totalUnread > 99 ? '99+' : totalUnread}</span>}
                  </Link>
                  <NotificationDropdown />
                </nav>
              </div>

              {/* Phone nav - visible only on phone */}
              <div className="header-nav-row">
                <nav className="nav">
                  <Link
                    to="/friends"
                    className={isActive('/friends') ? 'nav-link active' : 'nav-link'}
                    data-tooltip={t('friends.friends')}
                  >
                    {isActive('/friends') ? <IoPeopleSharp /> : <IoPeopleOutline />}
                    {pendingRequestsCount > 0 && <span className="nav-badge">{pendingRequestsCount > 99 ? '99+' : pendingRequestsCount}</span>}
                  </Link>
                  {hasDashboard && (
                  <Link
                    to="/admin"
                    className={isActive('/admin') ? 'nav-link active' : 'nav-link'}
                    data-tooltip="Dashboard"
                  >
                    {isActive('/admin') ? <IoStatsChartSharp /> : <IoStatsChartOutline />}
                  </Link>
                  )}
                  <Link
                    to="/chat"
                    className={isActive('/chat') ? 'nav-link active' : 'nav-link'}
                    data-tooltip={t('chat.messages')}
                  >
                    {isActive('/chat') ? <IoChatbubbleSharp /> : <IoChatbubbleOutline />}
                    {totalUnread > 0 && <span className="nav-badge">{totalUnread > 99 ? '99+' : totalUnread}</span>}
                  </Link>
                  <NotificationDropdown />
                </nav>

                <div className="user-menu" ref={phoneUserMenuRef}>
                  <button
                    className="user-menu-trigger"
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    aria-label={user?.display_name}
                  >
                    <Avatar user={user} size="small" />
                    <IoChevronDownOutline className={`user-menu-chevron ${userMenuOpen ? 'open' : ''}`} />
                  </button>
                  {userMenuOpen && (
                    <div className="user-dropdown">
                      <Link to={`/profile/${user?.id}`} className="user-dropdown-item" onClick={() => setUserMenuOpen(false)}>
                        <IoPersonOutline />
                        <span>{t('profile.goToProfile') || 'Go to profile'}</span>
                      </Link>
                      {hasDashboard && (
                        <Link to="/admin" className="user-dropdown-item" onClick={() => setUserMenuOpen(false)}>
                          <IoStatsChartOutline />
                          <span>{t('nav.dashboard') || 'Dashboard'}</span>
                        </Link>
                      )}
                      <Link
                        to="/terms"
                        className="user-dropdown-item"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <IoDocumentTextOutline />
                        <span>{t('footer.terms') || 'Terms of Use'}</span>
                      </Link>
                      <Link
                        to="/privacy"
                        className="user-dropdown-item"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <IoDocumentTextOutline />
                        <span>{t('footer.privacy') || 'Privacy Policy'}</span>
                      </Link>
                      <div className="user-dropdown-item language-item">
                        <IoGlobeOutline />
                        <LanguageSelector variant="dropdown" />
                      </div>
                      <button className="user-dropdown-item logout" onClick={() => { setUserMenuOpen(false); handleLogout(); }}>
                        <IoLogOutOutline />
                        <span>{t('auth.logout')}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <div className="header-actions">
            {isAuthenticated && (
              <div className="user-menu" ref={userMenuRef}>
                <button
                  className="user-menu-trigger"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  aria-label={user?.display_name}
                >
                  <Avatar user={user} size="small" />
                  <IoChevronDownOutline className={`user-menu-chevron ${userMenuOpen ? 'open' : ''}`} />
                </button>
                {userMenuOpen && (
                  <div className="user-dropdown">
                    <Link to={`/profile/${user?.id}`} className="user-dropdown-item" onClick={() => setUserMenuOpen(false)}>
                      <IoPersonOutline />
                      <span>{t('profile.goToProfile') || 'Go to profile'}</span>
                    </Link>
                    {hasDashboard && (
                      <Link to="/admin" className="user-dropdown-item" onClick={() => setUserMenuOpen(false)}>
                        <IoStatsChartOutline />
                        <span>{t('nav.dashboard') || 'Dashboard'}</span>
                      </Link>
                    )}
                    <Link
                      to="/terms"
                      className="user-dropdown-item"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <IoDocumentTextOutline />
                      <span>{t('footer.terms') || 'Terms of Use'}</span>
                    </Link>
                    <Link
                      to="/privacy"
                      className="user-dropdown-item"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <IoDocumentTextOutline />
                      <span>{t('footer.privacy') || 'Privacy Policy'}</span>
                    </Link>
                    <div className="user-dropdown-item language-item">
                      <IoGlobeOutline />
                      <LanguageSelector variant="dropdown" />
                    </div>
                    <button className="user-dropdown-item logout" onClick={() => { setUserMenuOpen(false); handleLogout(); }}>
                      <IoLogOutOutline />
                      <span>{t('auth.logout')}</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="main-content">{children}</main>
    </div>
  );
};

export default Layout;
